var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
// |                 | SICP    | Platform | Platform | Model      | Firmware |                                          |
// | Device          | Version | Label    | Version  | Number     | Version  | Notes                                    |
// |-----------------+---------+----------+----------+------------+----------+------------------------------------------|
// | Steve's Display |    1.88 |  Phoenix |      2.0 |  BDL3230QL |     1.31 |                                          |
// | Midwest .86     |   v2.03 |       QL |      3.0 | 65BDL3050Q |    05.06 | disconnects 10-20 seconds after power on |
// | Midwest .90     |   V2.04 | HIMALAYA |      2.0 | 98BDL4150D |    1.130 | laggy responses after power on           |
export class Philips {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.codeOffset = 0x30;
        this.deviceInfo = {};
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: '\x05\x01\x00\x19\x1d',
                next: /\x06\x01[\s\S]\x19[\x01\x02][\s\S]|\x06\x01[\s\S]\x00\x00\x18[\s\S]/,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                return (_a = this.deviceInfo.firmwareVersion) !== null && _a !== void 0 ? _a : 'unknown';
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x19]), /\x06\x01[\s\S]\x19([\s\S])[\s\S]/, tags);
                return match[1] === '\x02';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                let match = yield this.socket.send(this.makeCommand([0x18, power ? 0x02 : 0x01]), /\x06\x01[\s\S]\x00([\s\S])[\s\S]/, tags);
                if (match[1] !== '\x06')
                    return this.state.power.value;
                this.busyTimeout.clear();
                this.state.busy.update(true);
                if (((_b = this.deviceInfo.platformLabel) !== null && _b !== void 0 ? _b : '').toLowerCase() === 'ql' && power) {
                    // Some devices close their network socket when they power on. To work around this
                    // we reconnect the socket after a long delay that gives the display time to close
                    // its socket. This automatically suspends the framework's socket as well.
                    //
                    // The one device we have seen do this (Midwest .86) took about 20 seconds to close
                    // the network connection after the power on command.
                    this.framework.reconnect({
                        attemptDelayMs: 2000,
                        initialDelayMs: 23000,
                        numAttempts: 3,
                    });
                }
                else {
                    // On some displays, the api becomes laggy or unresponsive for some time after
                    // powering on. Sometimes it doesn't respond to queries, sometimes it resonds with
                    // a NAV. Sending too many commands during this time extends the duration of the
                    // unresponsivenes. To work around this, we wait a bit, then send power queries
                    // until we get a response. These values were tuned for the Midwest .90 device,
                    // but work fine and yield responsive behavior on the Midwest .86 device and
                    // Steve's device.
                    this.state.power.update(power); // give immediate feedback while we wait
                    this.framework.suspend(5500);
                    yield this.timeouts.delay(5000);
                    try {
                        match = yield this.socket.send(this.makeCommand([0x19]), /\x06\x01[\s\S]\x19([\s\S])[\s\S]/, ['get.power'], { retryAttempts: 12, sendTimeoutMs: 5000 });
                        power = match[1] === '\x02';
                    }
                    catch (err) {
                        this.log.error('timed out waiting for power query');
                    }
                    this.state.busy.update(false);
                }
                return power;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0xad]), /\x09\x01[\s\S]\xad([\s\S])[\s\S]{4}/, tags);
                return String.fromCharCode(match[1].charCodeAt(0) + this.codeOffset);
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const codeNum = code.charCodeAt(0) - this.codeOffset;
                const match = yield this.socket.send(this.makeCommand([0xac, codeNum, 0x00, 0x00, 0x00]), /\x06\x01[\s\S]\x00([\s\S])[\s\S]/, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 1000);
                return match[1] === '\x06' ? code : this.state.videoInput.value;
            }),
        };
        this.volume = {
            range: { max: 100, min: 0, round: 1 },
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // Himalaya and Eagle platforms don’t have variable audio out, so we allow data(2) to
                // be optional in the response.
                const match = yield this.socket.send(this.makeCommand([0x45]), /[\x07|\x06]\x01[\s\S]\x45([\s\S])[\s\S]+/, tags);
                return match[1].charCodeAt(0);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x44, volume, 0x00]), /\x06\x01[\s\S]\x00([\s\S])[\s\S]/, tags);
                const newVolume = match[1] === '\x06' ? volume : this.state.volume.value;
                return newVolume;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = { min: 0 };
        // polling
        this.polls = [
            { intervalMs: 2000, onePerInterval: true, controls: ['power', 'videoInput', 'volume'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
            this.deviceInfo = {};
            if (result === 'connected') {
                let match;
                try {
                    match = yield this.socket.send(this.makeCommand([0xa2, 0x00]), /[\s\S]\x01[\s\S]\xa2(.+)[\s\S]/);
                    this.deviceInfo.sicpVersion = match[1];
                }
                catch (err) { }
                try {
                    match = yield this.socket.send(this.makeCommand([0xa2, 0x01]), /[\s\S]\x01[\s\S]\xa2(.+)[\s\S]/);
                    this.deviceInfo.platformLabel = match[1];
                }
                catch (err) { }
                try {
                    match = yield this.socket.send(this.makeCommand([0xa2, 0x02]), /[\s\S]\x01[\s\S]\xa2(.+)[\s\S]/);
                    this.deviceInfo.platformVersion = match[1];
                }
                catch (err) { }
                try {
                    match = yield this.socket.send(this.makeCommand([0xa1, 0x00]), /[\s\S]\x01[\s\S]\xa1(.+)[\s\S]/);
                    this.deviceInfo.modelNumber = match[1];
                }
                catch (err) { }
                try {
                    match = yield this.socket.send(this.makeCommand([0xa1, 0x01]), /[\s\S]\x01[\s\S]\xa1(.+)[\s\S]/);
                    this.deviceInfo.firmwareVersion = match[1];
                }
                catch (err) { }
            }
            return result;
        });
    }
    // other class methods
    makeCommand(data) {
        const bytes = [
            data.length + 4,
            0x01,
            0x00,
            ...data,
        ];
        bytes.push(bytes.reduce((acc, curr) => acc ^ curr, 0)); // add checksum
        return AsciiText.decode(bytes);
    }
}
// static properties
Philips.driverType = 'socketDriver';
Philips.key = '-L7zG0u3-fjVmvA7601U';
Philips.baseSocketOptions = {
    commandExtractor: (buffer) => {
        // Command Format: [size] [control] [group] [data: 0-36 bytes] [checksum]
        // Try each byte in the buffer as a starting position in case we got some line noise.
        for (let i = 0; i < buffer.length - 3; i++) {
            // Make sure all the bytes for this command are present.
            if (buffer.length < i + buffer.charCodeAt(i))
                break;
            // Compute the checksum (XOR of all bytes except checksum byte)
            let checksum = 0;
            for (let j = 0; j < i + buffer.charCodeAt(i) - 1; j++) {
                checksum ^= buffer.charCodeAt(j);
            }
            // If the checksum is correct, then return the result.
            if (checksum === buffer.charCodeAt(i + buffer.charCodeAt(i) - 1)) {
                const command = buffer.slice(i, i + buffer.charCodeAt(i));
                buffer = buffer.slice(buffer.charCodeAt(i));
                return { command, buffer };
            }
        }
        return undefined;
    },
    isBinary: true,
    sendTerminator: '',
};
Philips.socketOptions = [
    Object.assign(Object.assign({}, Philips.baseSocketOptions), { port: 5000 }),
    Object.assign(Object.assign({}, Philips.baseSocketOptions), { port: 4999 }),
];
